import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { ApiError } from '@/lib/api/rawFetch'
import type { components } from '@/lib/api/generated'
import type { DashboardFilters } from '@/store/dashboardStore'

export type DashboardRow = components['schemas']['DashboardRow']
export type InventoryLocation = components['schemas']['InventoryLocation']

// Stable empty array so consumers can use the result directly as a memo dep
// without re-running on every render while the query is loading.
const NO_LOCATIONS: InventoryLocation[] = []

/** Shopify locations, used to build the grid's per-location stock columns.
 *  Only changes on an inventory sync, so it's cached for the session. */
export function useInventoryLocations(): InventoryLocation[] {
  const { data } = useQuery({
    queryKey: ['inventory-locations'] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/inventory/locations', {})
      if (error) throw new Error('Locations load failed')
      return data ?? NO_LOCATIONS
    },
    staleTime: 30 * 60_000,
  })
  return data ?? NO_LOCATIONS
}

// The 6 raw signal buckets the user wants exposed as filter options. Each maps
// to one (and only one) backend-emitted action_signal string from data_engine.py.
// The backend no longer emits "No Barcode" (a barcode-less variant gets a normal
// signal); any unrecognised string falls through to 'unknown'.
export type SignalKind = 'order' | 'liquidate' | 'ok' | 'no_action' | 'stop' | 'high_returns' | 'unknown'

export const SIGNAL_LABELS: Record<SignalKind, string> = {
  order: 'Order Now',
  liquidate: 'Liquidate',
  ok: 'Ok',
  no_action: 'No action',
  stop: 'Stop',
  high_returns: 'High returns',
  unknown: 'Unknown',
}

export function signalKind(actionSignal: string | null | undefined): SignalKind {
  if (!actionSignal) return 'unknown'
  const s = actionSignal.toUpperCase()
  if (s.includes('ORDER NOW')) return 'order'
  if (s.includes('HIGH RETURNS')) return 'high_returns'
  if (s.includes('LIQUIDATE')) return 'liquidate'
  if (s.includes('STOP')) return 'stop'
  if (s.includes('NO ACTION')) return 'no_action'
  if (s.includes('OK')) return 'ok'
  return 'unknown'
}

/** How often to re-ask while the backend is rebuilding the dashboard
 *  (matches the backend's Retry-After on the cold-start 503). */
export const DASHBOARD_POLL_MS = 15_000

/** True for the backend's cold-start 503 (`detail.code === "warming"`): the
 *  dashboard is still being built after a restart, not broken. */
export function isWarmingError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 503) return false
  const body = err.detail as { detail?: { code?: string } } | undefined
  return body?.detail?.code === 'warming'
}

interface DashboardPayload {
  rows: DashboardRow[]
  /** Rows are the previous build while a newer one builds after a sync. */
  refreshing: boolean
}

function applyClientFilters(rows: DashboardRow[], filters: DashboardFilters): DashboardRow[] {
  let out = rows

  if (filters.vendor.length > 0) {
    const set = new Set(filters.vendor)
    out = out.filter((r) => r.vendor && set.has(r.vendor))
  }
  if (filters.product_type.length > 0) {
    const set = new Set(filters.product_type)
    out = out.filter((r) => r.product_type && set.has(r.product_type))
  }
  if (filters.status.length > 0) {
    const set = new Set(filters.status.map((s) => s.toLowerCase()))
    out = out.filter((r) => r.status && set.has(r.status.toLowerCase()))
  }
  if (filters.signal.length > 0) {
    out = out.filter((r) => filters.signal.includes(signalKind(r.action_signal)))
  }
  if (filters.cov_min !== null) {
    out = out.filter((r) => (r.days_coverage ?? Infinity) >= filters.cov_min!)
  }
  if (filters.cov_max !== null) {
    out = out.filter((r) => (r.days_coverage ?? 0) <= filters.cov_max!)
  }
  if (filters.spike.length > 0) {
    out = out.filter((r) => filters.spike.includes(r.product_lifetime_spike ? 'flagged' : 'not_flagged'))
  }
  if (filters.spike_all.length > 0) {
    out = out.filter((r) =>
      filters.spike_all.includes(r.product_lifetime_spike_all ? 'flagged' : 'not_flagged'))
  }

  return out
}

export function useDashboard(filters: DashboardFilters) {
  // Archived variants are hidden by default (active + draft only) so the
  // default payload stays small. Only fetch them when the user explicitly
  // selects "Archived" in the Status filter — included in the query key so
  // toggling it refetches.
  const includeArchived = filters.status.some((s) => s.toLowerCase() === 'archived')

  const queryKey = [
    'dashboard',
    {
      search: filters.search || undefined,
      tags_include: filters.tags_include,
      tags_exclude: filters.tags_exclude,
      includeArchived,
    },
  ] as const

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET('/api/inventory/products', {
        params: {
          query: {
            search: filters.search || undefined,
            tags_include: filters.tags_include.length ? filters.tags_include : undefined,
            tags_exclude: filters.tags_exclude.length ? filters.tags_exclude : undefined,
            include_archived: includeArchived || undefined,
          },
        },
      })
      if (error) throw new ApiError(response.status, 'Dashboard load failed', error)
      const payload: DashboardPayload = {
        rows: data ?? [],
        refreshing: response.headers.get('X-Dashboard-Refreshing') === 'true',
      }
      return payload
    },
    staleTime: 5 * 60_000,
    // Keep asking while the backend rebuilds (stale rows being served) or is
    // still building after a restart; stop as soon as fresh data lands.
    refetchInterval: (q) =>
      q.state.data?.refreshing || isWarmingError(q.state.error) ? DASHBOARD_POLL_MS : false,
  })

  const rawRows = query.data?.rows

  // Memo by raw data identity + the client-filter inputs. Selection/sort
  // changes don't trigger a refilter.
  const filtered = useMemo(
    () => (rawRows ? applyClientFilters(rawRows, filters) : []),
    [
      rawRows,
      filters.vendor,
      filters.product_type,
      filters.status,
      filters.signal,
      filters.cov_min,
      filters.cov_max,
      filters.spike,
      filters.spike_all,
    ]
  )

  return {
    ...query,
    data: filtered,
    rawRows,
    rawCount: rawRows?.length ?? 0,
    refreshing: query.data?.refreshing ?? false,
    warming: isWarmingError(query.error),
  }
}
